import { CalendarIcon } from '@heroicons/react/24/outline';
import { useState, useCallback, useEffect, useRef, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'dayjs/locale/fr';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import Calendar from 'common/components/Calendar/Calendar';
import { Event, ModifiedEvent } from 'types/Event';
import useAuth from 'common/hooks/useAuth';
import useItem from 'common/hooks/useItem';
import useAsync from 'common/hooks/useAsync';
import fetchJSON from 'common/utils/fetchJSON';
import {
  calculateDateRangeForView,
  getDateFromRange,
  areDateRangesEqual,
} from 'common/components/Calendar/calendarUtils';
import Header from 'common/components/Header/Header';
import { generateRRuleString } from 'common/utils/rrule';
import { Views, NavigateAction } from 'react-big-calendar';
import AlertsContext from 'common/providers/alerts';
import { addEventListener, Listener, removeEventListener } from 'common/providers/SocketProvider';
import { formatDateForBackend } from 'common/utils/dateUtils';
import EventDrawer from './components/EventDrawer';

// Add required plugins for the localizer
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function Agenda() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { setAlert } = useContext(AlertsContext);

  // Default view of the calendar
  const calendarDefaultView = useMemo<'month' | 'week' | 'day' | 'agenda'>(() => 'week', []);

  // État pour suivre la date actuelle du calendrier
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<{
    start: Date;
    end: Date;
  }>({
    start: new Date(),
    end: new Date(),
  });
  const [events, setEvents] = useState<Event[]>([]);

  // État pour stocker les événements
  const [event, setEvent] = useState<Event | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ModifiedEvent | null>(null); // Event displayed during the modification

  // Référence pour suivre l'état du calendrier
  const calendarStateRef = useRef<{
    date: Date;
    view: 'month' | 'week' | 'day' | 'agenda';
    lastLoadedRange: {
      start: Date;
      end: Date;
      view: 'month' | 'week' | 'day' | 'agenda';
    } | null;
  }>({
    date: currentDate,
    view: calendarDefaultView,
    lastLoadedRange: null,
  });

  // Fonction pour calculer la plage de dates étendue en fonction de la vue
  const calculateExtendedDateRange = useCallback(
    (date: Date, view: 'month' | 'week' | 'day' | 'agenda') => {
      return calculateDateRangeForView(date, view);
    },
    []
  );

  // Fonction pour synchroniser les événements internes
  const fetchEventsAsync = useCallback(async () => {
    try {
      if (!dateRange.start || !dateRange.end) {
        // Don't clear events if dateRange is not set yet - events will be loaded when range is set
        return;
      }
      const response = await fetchJSON({
        url: 'events/me',
        method: 'POST',
        payload: {
          start: formatDateForBackend(dateRange.start),
          end: formatDateForBackend(dateRange.end),
        },
      });
      setEvents(response.data);

      // Mettre à jour l'événement sélectionné avec les nouvelles données
      setEvent((prevEvent) => {
        if (!prevEvent) return null;
        const foundEvent = response.data?.find((e: Event) => e.id === prevEvent?.id);
        return foundEvent || null;
      });
      setSelectedEvent((prevEvent) => {
        if (!prevEvent) return null;
        const foundEvent = response.data?.find((e: Event) => e.id === prevEvent?.id);
        return foundEvent ? ({ ...foundEvent, isModified: true } as ModifiedEvent) : null;
      });
    } catch (error) {
      console.error('Erreur lors de la synchronisation des événements internes:', error);
      setEvents([]);
    }
  }, [dateRange]);

  // Hook useAsync pour la synchronisation des événements interne
  const { execute: fetchEvents, isProcessing: isEventsFetching } = useAsync(fetchEventsAsync, {
    cachePrefix: 'events_planning',
    ready: Boolean(user?.operator),
    manual: true,
  });

  useEffect(() => {
    const listener: Listener = {
      events: ['meeting', 'events'],
      handleEvent: () => {
        fetchEvents();
      },
    };
    addEventListener(listener);

    return () => {
      removeEventListener(listener);
    };
  }, [fetchEvents]);

  // Utilisation de useAsync pour les disponibilités (availability)
  const fetchAvailabilitiesAsync = useCallback(
    async ({ start, end, operatorId }: { start: Date; end: Date; operatorId?: string }) => {
      try {
        const response = await fetchJSON({
          url: 'events/availability',
          method: 'POST',
          payload: {
            start: formatDateForBackend(start),
            end: formatDateForBackend(end),
            operatorId,
          },
        });

        return response.data;
      } catch (error) {
        console.error('Erreur lors de la récupération des disponibilités:', error);
        // Retourner un tableau vide en cas d'erreur
        return [];
      }
    },
    []
  );

  const {
    data: availabilities,
    isProcessing: isAvailabilitiesFetching,
    execute: fetchAvailabilities,
  } = useAsync<Event[]>(fetchAvailabilitiesAsync, {
    cachePrefix: 'availabilities_planning',
    ready: Boolean(user?.operator),
    storeData: true,
    manual: true,
  });

  // Fonction pour synchroniser les événements externes
  const syncExternalEventsAsync = useCallback(
    async (shouldForce = false) => {
      try {
        await fetchJSON({
          url: 'events/sync',
          method: 'POST',
          payload: {
            force: shouldForce,
          },
        });
        await fetchEvents();
      } catch (error) {
        console.error('Erreur lors de la synchronisation des événements externes:', error);
        throw error;
      }
    },
    [fetchEvents]
  );

  // Hook useAsync pour la synchronisation des événements externes
  const { execute: syncExternalEvents, isProcessing: isSyncingEvents } = useAsync(
    syncExternalEventsAsync,
    {
      manual: false, // Exécution automatique au chargement de la page
    }
  );

  const { saveItem, updateItem, removeItem } = useItem('events', null, {});

  const handleSelectEvent = useCallback(
    (newEvent: Event) => {
      setEvent(newEvent);
      setSelectedEvent({ ...newEvent, isModified: true });
    },
    [setEvent, setSelectedEvent]
  );

  const handleSelectSlot = useCallback(({ start, end }: { start: Date; end: Date }) => {
    // Convert the calendar's native dates to Europe/Paris timezone for consistent handling
    const parisStart = dayjs(start).tz('Europe/Paris');
    const parisEnd = dayjs(end).tz('Europe/Paris');

    const eventToBeCreated = {
      start: parisStart.toDate(),
      end: parisEnd.toDate(),
      allDay: false,
      title: '',
      isModified: true,
      meeting: undefined,
    } as ModifiedEvent;
    setEvent(eventToBeCreated);
    setSelectedEvent(eventToBeCreated);
  }, []);

  const handleSubmitEvent = useCallback(
    async (
      eventData: Event & {
        isRecurring: boolean;
        frequency: number;
        interval: number;
      }
    ) => {
      // eslint-disable-next-line no-param-reassign
      eventData.rrule = eventData.isRecurring
        ? generateRRuleString({
            isRecurring: eventData.isRecurring,
            frequency: eventData.frequency,
            interval: eventData.interval,
            start: eventData.start,
            repeatUntil: eventData.repeatUntil || null,
          }) || undefined
        : undefined;
      try {
        // First save or update the event
        const result = eventData.id
          ? await updateItem(eventData.id, eventData)
          : await saveItem(eventData);

        // Then refresh the events list
        await fetchEvents();

        // Return the original result
        return result;
      } catch (error) {
        console.error('Error creating/updating event:', error);
        throw error;
      }
    },
    [saveItem, updateItem, fetchEvents]
  );

  const handleDeleteEvent = useCallback(async () => {
    try {
      if (event && event.id && event.eventType !== 'meeting') {
        await removeItem(event.id);
        await fetchEvents();
        setEvent(null);
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  }, [event, fetchEvents, removeItem]);

  const handleDocumentAdded = useCallback(
    async (documentId: number) => {
      if (event?.meeting) {
        try {
          await fetchJSON({
            method: 'POST',
            url: `meetings/data/${event?.meeting?.id}/addDocument/${documentId}`,
            payload: {
              data: {
                file: documentId,
              },
            },
          });

          const newEvents = (await fetchEvents()) as Event[];
          const internalEvents = newEvents.filter((e) => !e.isExternalEvent);
          if (internalEvents) {
            setSelectedEvent((prevEvent) => {
              const foundEvent = internalEvents?.find((e) => e.id === prevEvent?.id);
              return foundEvent ? ({ ...foundEvent, isModified: true } as ModifiedEvent) : null;
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    },
    [event, fetchEvents]
  );

  const handleDocumentDeleted = useCallback(
    async (documentId: number) => {
      if (event?.meeting) {
        try {
          await fetchJSON({
            method: 'DELETE',
            url: `meetings/data/${event?.meeting?.id}/document/${documentId}`,
          });

          const newEvents = (await fetchEvents()) as Event[];
          const internalEvents = newEvents.filter((e) => !e.isExternalEvent);
          if (internalEvents) {
            setSelectedEvent((prevEvent) => {
              const foundEvent = internalEvents?.find((e) => e.id === prevEvent?.id);
              return foundEvent ? ({ ...foundEvent, isModified: true } as ModifiedEvent) : null;
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    },
    [event, fetchEvents]
  );

  // Fonction pour charger les événements pour une plage de dates étendue
  const loadEventsForExtendedRange = useCallback(
    async (date: Date, view: 'month' | 'week' | 'day' | 'agenda') => {
      if (!user?.operator) {
        return;
      }

      // Vérifier si les données sont déjà en cours de chargement
      if (isEventsFetching || isAvailabilitiesFetching) {
        return;
      }

      const { start, end } = calculateExtendedDateRange(date, view);

      // Stocker la plage de dates actuelle pour éviter des rechargements inutiles
      const currentRange: {
        start: Date;
        end: Date;
        view: 'month' | 'week' | 'day' | 'agenda';
      } = {
        start,
        end,
        view,
      };

      // Comparer avec la plage précédente pour éviter des rechargements inutiles
      const prevRange = calendarStateRef.current.lastLoadedRange;

      // Si la plage est la même, ne pas recharger
      if (prevRange && areDateRangesEqual(prevRange, currentRange)) {
        return;
      }

      // Mettre à jour la dernière plage chargée
      calendarStateRef.current.lastLoadedRange = currentRange;

      // Mettre à jour les filtres pour les événements
      setDateRange({ start, end });
      // fetchEvents();

      // Mettre à jour les filtres pour les disponibilités
      fetchAvailabilities({
        start,
        end,
        operatorId: user?.operator?.id || user?.operator,
      });
    },
    [
      user?.operator,
      calculateExtendedDateRange,
      fetchAvailabilities,
      isEventsFetching,
      isAvailabilitiesFetching,
    ]
  );

  // Effet initial pour charger les événements au démarrage
  useEffect(() => {
    if (user?.operator) {
      // Charger les événements pour la plage étendue au démarrage, uniquement au montage initial
      if (!calendarStateRef.current.lastLoadedRange) {
        loadEventsForExtendedRange(currentDate, calendarDefaultView);
      }
    }
  }, [user?.operator, loadEventsForExtendedRange, currentDate, calendarDefaultView]);

  // Fonction pour gérer le changement de plage de dates
  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      // Ce gestionnaire est maintenant utilisé uniquement pour les changements de plage
      // qui ne sont pas liés à la navigation (par exemple, redimensionnement de la fenêtre)

      // Pour la vue mois, on ignore les changements de plage car ils sont déjà gérés par handleNavigate
      if (calendarStateRef.current.view === 'month') {
        return;
      }

      // Pour les autres vues, on détermine la plage
      let newRange: { start: Date; end: Date };

      // Selon la documentation, le format de retour dépend de la vue
      if (Array.isArray(range)) {
        newRange = {
          start: range[0],
          end: range[range.length - 1],
        };
      } else {
        newRange = range;
      }

      // Utiliser la date du milieu de la plage pour le chargement
      const middleDate = getDateFromRange(newRange);

      // loadEventsForExtendedRange contient maintenant une vérification pour éviter les rechargements inutiles
      loadEventsForExtendedRange(
        middleDate,
        calendarStateRef.current.view as 'month' | 'week' | 'day' | 'agenda'
      );
    },
    [loadEventsForExtendedRange]
  );

  // Fonction pour gérer la navigation dans le calendrier
  const handleNavigate = useCallback(
    (date: Date, view?: Views, _action?: NavigateAction) => {
      // Mettre à jour la date actuelle dans la référence
      calendarStateRef.current.date = date;
      setCurrentDate(date);

      // Si la vue est fournie, la mettre à jour également
      if (view) {
        calendarStateRef.current.view = view as 'month' | 'week' | 'day' | 'agenda';
      }

      // Recharger les événements pour la nouvelle date
      loadEventsForExtendedRange(
        date,
        (view || calendarStateRef.current.view) as 'month' | 'week' | 'day' | 'agenda'
      );
    },
    [loadEventsForExtendedRange, setCurrentDate]
  );

  const handleEventDrop = useCallback(
    ({
      event: newEvent,
      start,
      end,
      isAllDay: droppedOnAllDaySlot = false,
    }: {
      event: Event;
      start: Date;
      end: Date;
      isAllDay: boolean;
    }) => {
      // Empêcher le déplacement des événements externes
      if (newEvent?.isExternalEvent) {
        setAlert(t('agenda.externalEventModificationForbidden'), 'danger');
        return;
      }

      const { allDay } = newEvent;
      if (!allDay && droppedOnAllDaySlot) {
        // eslint-disable-next-line no-param-reassign
        newEvent.allDay = true;
      }
      if (allDay && !droppedOnAllDaySlot) {
        // eslint-disable-next-line no-param-reassign
        newEvent.allDay = false;
      }

      handleSubmitEvent({ ...newEvent, start, end, allDay, recurrenceEditMode: 'single' } as any);
    },
    [handleSubmitEvent, t, setAlert]
  );

  const handleEventResize = useCallback(
    ({ event: newEvent, start, end }: { event: Event; start: Date; end: Date }) => {
      // Empêcher le redimensionnement des événements externes
      if (newEvent.isExternalEvent) {
        setAlert(t('agenda.externalEventModificationForbidden'), 'danger');
        return;
      }

      handleSubmitEvent({ ...newEvent, start, end, recurrenceEditMode: 'single' } as any);
    },
    [handleSubmitEvent, t, setAlert]
  );

  return (
    <div className="py-6 px-6">
      <div className="mx-auto text-sm max-w-full max-main-box mb-2">
        <Header
          title={t('titlePages.planning')}
          subTitle={t('titleLegends.planning')}
          Icon={CalendarIcon}
          synchronizeAction={() => {
            syncExternalEvents(true);
          }}
          synchronizeLabelKey="actions.synchronizeAllEvents"
          synchronizeActionDisabled={isSyncingEvents}
        />
      </div>

      <div className="mt-6 mx-auto text-sm max-w-full max-main-box">
        <div className="bg-white shadow-sm rounded-md overflow-hidden">
          <div className="p-4 ">
            <Calendar
              activeViews={{
                month: true,
                week: true,
                day: true,
                agenda: true,
              }}
              defaultView={calendarDefaultView}
              events={
                !events
                  ? []
                  : selectedEvent
                  ? [...events.filter((e) => e.id !== selectedEvent.id), selectedEvent]
                  : events
              }
              backgroundEvents={availabilities || []}
              selectable
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              onNavigate={handleNavigate}
              onRangeChange={handleRangeChange}
              date={currentDate}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
            />
          </div>
        </div>
      </div>

      <EventDrawer
        event={event}
        setEvent={setEvent}
        setSelectedEvent={setSelectedEvent}
        onSubmit={handleSubmitEvent}
        newEventType="personal"
        onEventChanged={fetchEvents}
        onDeleteEvent={handleDeleteEvent}
        onDocumentAdded={handleDocumentAdded}
        onDocumentDeleted={handleDocumentDeleted}
      />
    </div>
  );
}

export default Agenda;
